import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import type { ConceptChipApi } from '@mathflat/domain/@entities/ConceptChip/api'
import { Entity as ConceptChipEntity } from '@mathflat/domain/@entities/ConceptChip/dto'
import { CURRICULUM_KEYS } from '@mathflat/domain/@entities/Curriculum/constant'
import { flatMap } from 'lodash-es'
import { makeAutoObservable } from 'mobx'

import { achievementAnalysisController } from '@/controller/achievementAnalysis.controller'
import type { Student } from '@/dto'
import commonRepo from '@/repository/common.repo'
import type { DateStr } from '@/types'
import { _mixpanelUtils } from '@/utils/trackings/_mixpanel'

import type {
  AchievementAnalysisService,
  SchoolTestDataForService,
} from './AchievementAnalysis.service'

const DEFAULT_FILTER_DATA = {
  curriculumKey: CURRICULUM_KEYS.CURRICULUM_KEY_초등학교_1학년_1학기, // 초1
  requestDate: null,
  workbookList: {
    CUSTOM_SIGNATURE: [],
    ACADEMY_GROUP: [],
    PUBLIC: [],
    SCHOOL: [],
  },
  schoolTests: [],
}

export class AchievementConceptChipManager {
  private _resConceptChips: res.ConceptChip[] = []
  private _resAchievements: res.Achievement[] = []

  private _achievementChipMap: Map<res.Achievement['conceptChipId'], res.Achievement> = new Map()

  achievementConceptChips: ConceptChipEntity.AchievementConceptChip[] = []

  private _filter: {
    // 학년 학기
    curriculumKey: string
    requestDate: DateStr<'-'> | null
    workbookList: {
      [key in keyof AchievementAnalysisService['workbookResponseFilterColls']]: WorkbookEntity.Workbook<key>[]
    }
    schoolTests: SchoolTestDataForService[]
  } = DEFAULT_FILTER_DATA

  get filter() {
    return this._filter
  }

  get workbookIds() {
    return flatMap(this._filter.workbookList).map((workbook) => workbook.id)
  }
  get schoolTestIds() {
    // 매쓰플랫인 경우에만 학교별 기출을 반영
    const schoolTestIds = !commonRepo.isSchoolflat
      ? this._filter.schoolTests.map((schoolTest) => schoolTest.id)
      : []
    return schoolTestIds
  }
  constructor() {
    makeAutoObservable(this)
  }

  updateFilter(data: Partial<AchievementConceptChipManager['_filter']>) {
    this._filter = { ...this._filter, ...data }
  }

  resetFilter() {
    this._filter = DEFAULT_FILTER_DATA
  }

  // 그룹 유형분석 conceptChip fetch
  async fetchGroupConceptChips(params: ConceptChipApi.Request.GroupKeyAndInfo) {
    const achievementConceptChips =
      await achievementAnalysisController.getGroupConceptChipsAndAchievements({
        conceptChipCurriculumKey: this._filter.curriculumKey,
        workbookIds: this.workbookIds,
        schoolTestIds: this.schoolTestIds,
        ...params,
      })

    return (this.achievementConceptChips = achievementConceptChips)
  }

  // 그룹 유형분석 과거 성취도 fetch
  async fetchGroupPastAchievements(
    params: ConceptChipApi.Request.GroupKeyAndInfo & { requestDate: DateStr<'-'> },
  ) {
    const achievementConceptChips = await achievementAnalysisController.getGroupPastAchievements({
      conceptChipCurriculumKey: this._filter.curriculumKey,
      workbookIds: this.workbookIds,
      schoolTestIds: this.schoolTestIds,
      ...params,
    })
    return (this.achievementConceptChips = achievementConceptChips)
  }

  async fetchAllAndMakeChips(studentId: Student['id']) {
    await this._fetchAchievements(studentId)
    await this._fetchConceptChips()
    this._makeAchievementConceptChipMap()
    this._makeAchievementConceptChips()
  }

  async fetchAchievementAndMakeChips(studentId: Student['id']) {
    await this._fetchAchievements(studentId)

    this._makeAchievementConceptChipMap()
    this._makeAchievementConceptChips()
  }

  async fetchConceptChipAndMakeChips() {
    await this._fetchConceptChips()
    this._schoolTestDataTracking()
    this._makeAchievementConceptChipMap()
    this._makeAchievementConceptChips()
  }
  private _schoolTestDataTracking() {
    try {
      if (this.schoolTestIds?.length) {
        _mixpanelUtils.uiTrack('유형분석_필터_학교별기출')
      }
    } catch (e) {
      console.error(e)
    }
  }
  /* --- private fetch method --- */
  // 컨셉칩 fetch (교재 및 학교별 기출을 반영해서)
  private async _fetchConceptChips() {
    const { data: resConceptChips } = await achievementAnalysisController.getConceptChips({
      curriculumKey: this._filter.curriculumKey,
      workbookIds: this.workbookIds,
      schoolTestIds: this.schoolTestIds,
    })
    return (this._resConceptChips = resConceptChips)
  }

  private async _fetchAchievements(studentId: Student['id']) {
    // 조회 날짜가 있는 경우에는 과거 이력 조회 Api 를 호출해야 하고 아닌 경우에는 기본 Api 호출
    if (!this._filter.requestDate) {
      const { data: resAchievements } = await achievementAnalysisController.getAchievements(
        {
          curriculumKey: this._filter.curriculumKey,
          requestDate: this._filter.requestDate,
        },
        studentId,
      )
      return (this._resAchievements = resAchievements)
    }

    const { data: resAchievements } = await achievementAnalysisController.getPastAchievements(
      {
        curriculumKey: this._filter.curriculumKey,
        requestDate: this._filter.requestDate,
      },
      studentId,
    )

    return (this._resAchievements = resAchievements)
  }

  private _makeAchievementConceptChipMap() {
    this._achievementChipMap = new Map(
      this._resAchievements.map((achievement) => [achievement.conceptChipId, achievement]),
    )
  }

  private _makeAchievementConceptChips() {
    this.achievementConceptChips = this._resConceptChips
      .map((conceptChip) => {
        return new ConceptChipEntity.AchievementConceptChip({
          id: conceptChip.conceptChipId,
          orderingNumber: conceptChip.orderNumber,
          conceptChipId: conceptChip.conceptChipId,
          conceptChipType: conceptChip.conceptChipType,
          recommended: conceptChip.recommended,
          bigChapterId: conceptChip.bigChapterId,
          bigChapterName: conceptChip.bigChapterName,
          middleChapterId: conceptChip.middleChapterId,
          middleChapterName: conceptChip.middleChapterName,
          littleChapterId: conceptChip.littleChapterId,
          littleChapterName: conceptChip.littleChapterName,
          conceptId: conceptChip.conceptId,
          conceptName: conceptChip.conceptName,
          repProblem: conceptChip.repProblem,
          achievementLevel:
            this._achievementChipMap.get(conceptChip.conceptChipId)?.achievementLevel ?? 'WHITE',
          studentId: this._achievementChipMap.get(conceptChip.conceptChipId)?.studentId,
          latestStudyLog: this._achievementChipMap.get(conceptChip.conceptChipId)?.latestStudyLog,
        })
      })
      .sort((x, y) => x.orderingNumber - y.orderingNumber)
  }
}
